import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ArrowLeft, Heart, Share2 } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(response.data);
      } catch (error) {
        console.error("Failed to fetch project:", error);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id, token, navigate]);

  const handleLike = async () => {
    if (liked) return;
    try {
      await axios.post(`${API}/projects/${id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(prev => ({ ...prev, likes: (prev.likes || 0) + 1 }));
      setLiked(true);
    } catch (error) {
      console.error("Failed to like:", error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: project.description,
          url: window.location.href
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Skeleton className="aspect-[4/5] w-full bg-zinc-800" />
        <div className="p-4 space-y-4">
          <Skeleton className="h-6 w-3/4 bg-zinc-800" />
          <Skeleton className="h-20 w-full bg-zinc-800" />
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="project-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-16 left-4 z-30 w-10 h-10 rounded-full bg-black/80 backdrop-blur-md flex items-center justify-center text-white"
        data-testid="back-button"
      >
        <ArrowLeft size={20} />
      </button>

      {/* Image Gallery */}
      <div className="relative">
        <div className="aspect-[4/5] bg-zinc-900 overflow-hidden">
          <img
            src={project.images?.[selectedImage] || "https://images.unsplash.com/photo-1760072513403-d70003481414?w=800"}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Image Indicators */}
        {project.images?.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {project.images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  selectedImage === idx ? "bg-white w-4" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`transition-all ${liked ? "text-red-500 scale-110" : "text-white hover:text-white/70"}`}
              data-testid="like-btn"
            >
              <Heart size={28} strokeWidth={1.5} fill={liked ? "currentColor" : "none"} />
            </button>
            <button
              onClick={handleShare}
              className="text-white hover:text-white/70 transition-colors"
              data-testid="share-btn"
            >
              <Share2 size={28} strokeWidth={1.5} />
            </button>
          </div>
          <span className="text-sm text-white/60">{project.likes || 0} Mi piace</span>
        </div>

        {/* Category */}
        <p className="text-xs text-emerald-500 uppercase tracking-wider">{project.category}</p>

        {/* Title */}
        <h1 className="text-xl font-light text-white">{project.title}</h1>

        {/* Description */}
        <p className="text-sm text-white/70 leading-relaxed">{project.description}</p>

        {/* Date */}
        <p className="text-xs text-white/40 uppercase tracking-wider">
          Pubblicato il {new Date(project.created_at).toLocaleDateString("it-IT", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })}
        </p>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
